import React, { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames';
import moment from 'moment'
import _ from 'lodash';

import AttachmentView from './AttachmentView';
import DeleteDigitalLetter from './DeleteDigitalLetter';
import ParseHTML from '../../components/ParseHTML';
import Pagination from '../../components/Pagination';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { clearResetBot,setInboxLetterDetails,showAttachmentFunc,setGeneralLetterDetails } from '../../../config/redux/slice/appSlice'
import { appUrls } from '../../../utils/urls/appUrls';
import { useForm } from 'react-hook-form';
import { getInboxLetters, readLetter } from '../../../config/redux/actions/app';
import { Spinner } from '../../components';
import { convertDateTime, paginate } from '../../../utils/lib';
import { getStaffList } from '../../../config/redux/actions/notap';

function DigitalLetterInbox() {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { register, handleSubmit, watch } = useForm({mode: 'onTouched', defaultValues: { inbox_status: "all" }})
    const { userLoading, digitalLetterManagement: {inboxLetters, showAttachmentModal, newLetter, showDeleteConfirmationForm} } = useSelector((state) => state.app)

    const pageSize = 10;

    const status = watch("inbox_status");

    const [inboxDetails, setInboxDetails] = useState(null)
    const [currentPage, setCurrentPage] = useState(1);

    const paginatedLetters = useMemo(() => {
      const sorted = _.orderBy(inboxLetters, ['CREATED_AT'], ['desc']);
      const paginated = paginate(sorted, currentPage, pageSize);

      return paginated;
    }, [currentPage, inboxLetters]);

    const renderDate = useCallback((letter) => {
      const localTimeCreated = convertDateTime(letter.CREATED_AT);
      const isToday = moment(localTimeCreated).isSame(moment(), 'day');
      
      return convertDateTime(letter.CREATED_AT, isToday ? 'MMM DD HH:mm a' : 'MMM DD');
    }, []);

    const viewDetails = (letterObject) => {
        dispatch(readLetter(letterObject.ID_1));

        dispatch(setGeneralLetterDetails({details: letterObject, modal: true}));
        dispatch(setInboxLetterDetails({details: letterObject, modal: true}));
        navigate(`${appUrls.digitalLetterBase}?tab=inbox&details=true`)
    };

    const refetchLetters = useCallback(() => {
      dispatch(getInboxLetters(status));
    }, [dispatch, status]);

    useEffect(() => {
      refetchLetters()
    }, [refetchLetters, newLetter]);

    useEffect(() => {
      if (newLetter) dispatch(clearResetBot());
    }, [dispatch, newLetter]);

    useEffect(() => {
      dispatch(getStaffList());
    }, [dispatch]);

    return (
      <>
        {showDeleteConfirmationForm && inboxDetails && (
          <DeleteDigitalLetter
              adjective={'from'}
              email={inboxDetails.email}
              id={inboxDetails.id}
              subject={inboxDetails.subject}
              url={inboxDetails.url}
          />
        )}

        {showAttachmentModal && <AttachmentView filePath={inboxDetails?.filePath} />}

        <div className="email-right-column p-4 p-xl-5">
          <div className="email-right-header mb-5">
            <div style={{ columnGap: '2rem' }} className="head-left-options">
              <button title="Refresh" type="button" className="btn btn-icon btn-outline btn-rounded-circle" onClick={refetchLetters}>
                <i className="mdi mdi-refresh"></i>
              </button>

              <form onSubmit={handleSubmit((data) => dispatch(getInboxLetters(data.inbox_status)))}>
                <div className='row align-items-center'>
                  <div className="col-8">
                    <select 
                      className="form-control rounded-0" 
                      id="inbox_status"
                      {...register("inbox_status")} 
                    >
                      <option value=''>Select Status</option>
                      <option value='all'>All</option>
                      <option value='pending'>Pending</option>
                    </select>
                  </div>
                
                  <div className="col-4">
                      <button disabled={userLoading} type="submit" className="btn btn-sm btn-primary mb-1 text-white">Apply</button>
                  </div>
                </div>
              </form>
            </div>

            <div style={{ columnGap: '0.8rem' }} className="head-right-options d-flex align-items-center">
              {inboxLetters.length > 0 && (
                <div>
                  {((currentPage * pageSize) - pageSize) + 1} - {inboxLetters.length < pageSize ? inboxLetters.length : currentPage * pageSize} of {inboxLetters.length}
                </div>
              )}

              <Pagination currentPage={currentPage} itemsCount={inboxLetters.length} pageSize={pageSize} onPageChange={(page) => setCurrentPage(page)} />
            </div>
          </div>

          <div className="border border-top-0 rounded table-responsive email-list">
            {userLoading && (
              <div className="d-flex justify-content-center">
                <Spinner />
              </div>
            )}
            
            {inboxLetters.length === 0 
              ? <p className='text-center mt-5'>You don't have any inbox letters</p> 
              : (
                <table style={{width: '100%'}} className="table mb-0 table-email">
                  <tbody>
                    {paginatedLetters.map((inboxLetter, index) => (
                      <tr key={index} onClick={() => viewDetails(inboxLetter)} className={classNames({ "letter-table-row": true, "unread": inboxLetter.IS_NEW === 1, "read": inboxLetter.IS_NEW === 0 })}>
                          <td className="star">
                            <div className='email-avatar'>
                              <p className='text-capitalize'>{inboxLetter.SENDER_NAME_1[0]}</p>
                            </div>
                          </td>

                          <td className="sender-name text-dark">
                            <span className='d-inline-block' style={{width: '187px'}} >{inboxLetter?.SENDER_ID_1}</span>
                          </td>

                          <td>
                            <span className="text-default d-inline-block text-smoke">
                              <span className={`${inboxLetter?.IS_NEW ? 'subject text-dark' : ''}`}>
                                {inboxLetter?.LETTER_SUBJECT ? inboxLetter?.LETTER_SUBJECT : '(no subject)'} -
                              </span>
                              
                              {inboxLetter && <ParseHTML inputHtml={inboxLetter?.LETTER_CONTENTS}/>}
                            </span>
                          </td>
                        
                          <td>
                            <i className={classNames({
                              "mdi mdi-paperclip": true,
                              "attachment": inboxLetter?.ATTACH_DOCUMENTS,
                              "hidden-on-hover": false
                            })}></i>
                          </td>
                      
                          <td className="date">
                            <div className='show-on-hover'>
                              {inboxLetter?.ATTACH_DOCUMENTS && (
                                <button 
                                  style={{fontSize: '1rem', color: 'black', border: '1px solid #e5e9f2'}} 
                                  className='mr-1 btn btn-icon btn-outline btn-rounded-circle'
                                  onClick={(e) => {
                                    e.stopPropagation();

                                    setInboxDetails({ filePath: inboxLetter?.ATTACH_DOCUMENTS });
                                    dispatch(showAttachmentFunc({ modal: true }))
                                  }}
                                >
                                  
                                  <i className="mdi mdi-paperclip"></i>
                                </button>
                              )}
                            </div>
                            
                            <div className='hidden-on-hover text-uppercase'>
                              {renderDate(inboxLetter)}
                            </div>
                          </td>
                        </tr>
                    ))}
                  </tbody>
                </table>
              )}  
          </div>
        </div>
      </>
    );
}

export default DigitalLetterInbox;
